import {
  ArrowRight,
  Play,
  User,
  Wand2,
  Settings,
  Package,
  Zap,
  BookOpen,
  Plus,
  Share2,
} from "lucide-react"
import { Switch } from "@/components/ui/switch"
import type { AgentMockupConfig } from "../types"

interface AgentBuilderMockupProps {
  /** Configuration for the agent mockup */
  config: AgentMockupConfig
}

/**
 * Agent Builder Mockup component that visually represents the agent builder interface.
 * This is a non-functional visual representation used for marketing and demo purposes.
 * Accepts configuration props to customize the display for different use cases.
 */
export function AgentBuilderMockup({ config }: AgentBuilderMockupProps) {
  return (
    <div className="w-full rounded-xl border border-neutral-700 bg-neutral-900 shadow-2xl overflow-hidden">
      {/* Header Bar */}
      <div className="flex items-center justify-between border-b border-neutral-800 px-4 py-2.5">
        <div className="flex items-center gap-3">
          <div className="h-6 w-6 rounded bg-neutral-800 flex items-center justify-center">
            <ArrowRight className="h-3 w-3 rotate-180 text-neutral-500" />
          </div>
          <span className="text-sm font-medium text-white">{config.agentName}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="rounded bg-neutral-800 px-2.5 py-1 text-xs text-neutral-400 flex items-center gap-1">
            <Play className="h-3 w-3" />
            Test Agent
          </div>
          <div className="rounded bg-secondary/90 px-2.5 py-1 text-xs text-white flex items-center gap-1">
            <Share2 className="h-3 w-3" />
            Publish
          </div>
          <div className="rounded bg-primary px-2.5 py-1 text-xs text-white">
            Save Agent
          </div>
        </div>
      </div>

      {/* 3-Column Layout */}
      <div className="grid grid-cols-12 min-h-[320px]">
        {/* Left Column - Profile Section */}
        <div className="col-span-5 border-r border-neutral-800 p-4">
          {/* Avatar and Name */}
          <div className="flex items-center gap-3 mb-4">
            <div className="relative">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-primary/30 to-primary/10">
                <User className="h-6 w-6 text-primary" />
              </div>
            </div>
            <div className="flex-1">
              <div className="text-xs text-neutral-500 mb-1">Agent Name *</div>
              <div className="h-8 rounded border border-neutral-700 bg-neutral-800 px-2.5 flex items-center">
                <span className="text-sm text-white">{config.agentName}</span>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="mb-4">
            <div className="text-xs text-neutral-500 mb-1">Description *</div>
            <div className="relative h-16 rounded border border-neutral-700 bg-neutral-800 p-2.5">
              <div className="text-xs text-neutral-400 line-clamp-3">
                {config.description}
              </div>
              <div className="absolute bottom-1.5 right-1.5">
                <Wand2 className="h-3.5 w-3.5 text-neutral-500" />
              </div>
            </div>
          </div>

          {/* System Instructions */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-neutral-500">System Instructions</span>
              <div className="flex items-center gap-1 text-xs text-neutral-500">
                <Wand2 className="h-3 w-3" />
                <span>Enhance</span>
              </div>
            </div>
            <div className="rounded border border-neutral-700 bg-neutral-950 p-3 font-mono text-xs">
              {config.systemPromptLines.map((line, i) => (
                <div
                  key={i}
                  className={
                    line.startsWith("#")
                      ? "text-neutral-500 mb-1"
                      : line.startsWith("-")
                      ? "text-neutral-400"
                      : "text-purple-400 mb-1"
                  }
                >
                  {line}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Middle Column - Capabilities */}
        <div className="col-span-3 border-r border-neutral-800">
          {/* Model Selector */}
          <div className="flex items-center gap-2 border-b border-neutral-800 px-3 py-2.5">
            <div className="flex-1 rounded border border-neutral-700 bg-neutral-800 px-2.5 py-1.5 text-xs text-white flex items-center gap-2">
              <div className="h-4 w-4 rounded bg-emerald-500/20 flex items-center justify-center">
                <span className="text-xs text-emerald-400">AI</span>
              </div>
              {config.model}
            </div>
            <Settings className="h-4 w-4 text-neutral-500" />
          </div>

          {/* Features */}
          <div className="space-y-0">
            {config.features.map((feature, i) => (
              <div
                key={i}
                className="flex items-center justify-between border-b border-neutral-800 px-3 py-2.5"
              >
                <span className="text-xs text-neutral-300">{feature.label}</span>
                <Switch checked={feature.enabled} className="scale-75" disabled />
              </div>
            ))}
          </div>

          {/* Tools Header */}
          <div className="border-b border-neutral-800 px-3 py-2 bg-neutral-800/50">
            <span className="text-xs font-medium uppercase tracking-wide text-neutral-500">
              Built-in Tools
            </span>
          </div>

          {/* Tools */}
          <div className="space-y-0">
            {config.tools.map((tool, i) => (
              <div
                key={i}
                className="flex items-center justify-between border-b border-neutral-800 px-3 py-2.5"
              >
                <span className="text-xs text-neutral-300">{tool.label}</span>
                <Switch checked={tool.enabled} className="scale-75" disabled />
              </div>
            ))}
          </div>
        </div>

        {/* Right Column - Connections */}
        <div className="col-span-4 p-4 space-y-4">
          {/* Integrations */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5 text-xs text-neutral-300">
                <Package className="h-3.5 w-3.5" />
                Integrations
              </div>
              <div className="flex items-center gap-1 text-xs text-primary">
                <Plus className="h-3 w-3" />
                Add
              </div>
            </div>
            <div className="grid grid-cols-4 gap-1.5">
              {config.integrations.slice(0, 4).map((app, i) => (
                <div
                  key={i}
                  className="flex h-8 items-center justify-center rounded border border-neutral-700 bg-neutral-800 text-xs text-neutral-400"
                >
                  {app}
                </div>
              ))}
            </div>
          </div>

          {/* Triggers */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5 text-xs text-neutral-300">
                <Zap className="h-3.5 w-3.5" />
                Triggers
              </div>
              <div className="flex items-center gap-1 text-xs text-primary">
                <Plus className="h-3 w-3" />
                Add
              </div>
            </div>
            {config.triggers.slice(0, 2).map((trigger, i) => (
              <div
                key={i}
                className="rounded border border-neutral-700 bg-neutral-800 p-2.5 mb-1.5"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs text-neutral-300">{trigger.label}</span>
                  <span className="rounded bg-primary/20 px-1.5 py-0.5 text-xs text-primary">
                    {trigger.source}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Knowledge */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5 text-xs text-neutral-300">
                <BookOpen className="h-3.5 w-3.5" />
                Knowledge
              </div>
              <div className="flex items-center gap-1 text-xs text-primary">
                <Plus className="h-3 w-3" />
                Add
              </div>
            </div>
            {config.knowledgeBases.slice(0, 2).map((kb, i) => (
              <div
                key={i}
                className={`rounded border p-2.5 mb-1.5 ${
                  i === 0
                    ? "border-primary/30 bg-primary/5"
                    : "border-neutral-700 bg-neutral-800"
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className="rounded bg-neutral-800 p-1.5">
                    <BookOpen className="h-3 w-3 text-neutral-400" />
                  </div>
                  <div>
                    <div className="text-xs text-neutral-300">{kb.name}</div>
                    <div className="text-xs text-neutral-500">
                      {kb.chunks.toLocaleString()} chunks
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
